import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { WpPage } from '@audit/shared-types';

type SortField = 'title' | 'type' | 'slug' | 'wordpressModifiedAt';
type SortDirection = 'asc' | 'desc';

function formatLastUpdated(value?: string): string {
  if (!value) {
    return 'Not available';
  }

  return new Date(value).toLocaleString();
}

export default function PagesPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const queryClient = useQueryClient();
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ['pages', siteId],
    queryFn: () =>
      apiClient.get<WpPage[]>(`/pages?siteId=${siteId ?? ''}`).then((r) => r.data),
    enabled: !!siteId,
  });

  const syncPagesMutation = useMutation({
    mutationFn: async () => {
      if (!siteId) {
        throw new Error('siteId is required');
      }

      return apiClient.post('/pages/sync', { siteId: Number(siteId) });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pages', siteId] });
    },
  });

  const sortedPages = useMemo(() => {
    return [...pages].sort((left, right) => {
      let comparison = 0;

      if (sortField === 'wordpressModifiedAt') {
        const leftValue = left.wordpressModifiedAt ? new Date(left.wordpressModifiedAt).getTime() : Number.NEGATIVE_INFINITY;
        const rightValue = right.wordpressModifiedAt ? new Date(right.wordpressModifiedAt).getTime() : Number.NEGATIVE_INFINITY;
        comparison = leftValue - rightValue;
      } else {
        comparison = left[sortField].localeCompare(right[sortField]);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [pages, sortDirection, sortField]);

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const renderSortLabel = (label: string, field: SortField) => {
    const isActive = sortField === field;
    const indicator = !isActive ? '' : sortDirection === 'asc' ? ' ↑' : ' ↓';

    return (
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className="inline-flex items-center gap-1 hover:text-gray-700"
      >
        <span>{label}{indicator}</span>
      </button>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Pages &amp; Posts</h2>
        <button
          onClick={() => void syncPagesMutation.mutateAsync()}
          disabled={!siteId || syncPagesMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 disabled:bg-gray-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {syncPagesMutation.isPending ? 'Syncing…' : 'Sync pages from WordPress'}
        </button>
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading pages…</p>}
      {syncPagesMutation.isError && (
        <p className="text-sm text-red-600 mb-4">Failed to sync pages from WordPress</p>
      )}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50">
              <th className="px-6 py-3">{renderSortLabel('Title', 'title')}</th>
              <th className="px-6 py-3">{renderSortLabel('Type', 'type')}</th>
              <th className="px-6 py-3">{renderSortLabel('Slug', 'slug')}</th>
              <th className="px-6 py-3">{renderSortLabel('Last updated', 'wordpressModifiedAt')}</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedPages.map((page) => (
              <tr key={page.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-6 py-3 font-medium text-gray-900">{page.title}</td>
                <td className="px-6 py-3 text-gray-500 capitalize">{page.type}</td>
                <td className="px-6 py-3 text-gray-400 font-mono text-xs">{page.slug}</td>
                <td className="px-6 py-3 text-gray-500 whitespace-nowrap">{formatLastUpdated(page.wordpressModifiedAt)}</td>
                <td className="px-6 py-3">
                  <Link
                    to={`/sites/${siteId ?? ''}/pages/${page.id}/audit`}
                    className="text-brand-600 hover:text-brand-700 text-xs font-medium"
                  >
                    Run audit →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && pages.length === 0 && (
          <div className="p-6 text-sm text-gray-500">No pages found. Sync pages from WordPress first.</div>
        )}
      </div>
    </div>
  );
}
